import { useEffect, useState } from 'react';
import { Badge, Button, Modal } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { DocBlock, DocRow, DocInlineDate } from '../../components/document';
import { StavbaGate } from '../../components/stavba';
import {
  useRfi,
  useCreateRfi,
  useRfiDetail,
  useUpdateRfi,
  useDeleteRfi,
} from '../../api/hooks';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import type { Rfi, RfiStav, RfiKategorie, Priorita } from '../../api/schemas';

const stavMap: Record<RfiStav, { tone: 'yellow' | 'success' | 'neutral'; label: string }> = {
  otevreno: { tone: 'yellow', label: 'Čeká na odpověď' },
  zodpovezeno: { tone: 'success', label: 'Odpovězeno' },
  uzavreno: { tone: 'neutral', label: 'Uzavřeno' },
};

const KATEGORIE_LABEL: Record<RfiKategorie, string> = {
  projekt: 'Projekt',
  material: 'Materiál',
  termin: 'Termín',
  technologie: 'Technologie',
  jine: 'Jiné',
};

export function RfiPage() {
  const { activeStavba } = useActiveStavba();
  const stavbaId = activeStavba?.id ?? null;

  const { data: items = [], isLoading, error } = useRfi(stavbaId);
  const create = useCreateRfi(stavbaId);
  const update = useUpdateRfi(stavbaId);
  const del = useDeleteRfi(stavbaId);

  const [openNew, setOpenNew] = useState(false);
  const [predmet, setPredmet] = useState('');
  const [popis, setPopis] = useState('');
  const [kategorie, setKategorie] = useState<RfiKategorie>('projekt');
  const [priorita, setPriorita] = useState<Priorita>('stredni');
  const [adresat, setAdresat] = useState('');
  const [termin, setTermin] = useState('');
  const [odkazVykres, setOdkazVykres] = useState('');

  const [editId, setEditId] = useState<number | null>(null);
  const detail = useRfiDetail(stavbaId, editId);
  const [draft, setDraft] = useState<Rfi | null>(null);
  useEffect(() => {
    if (detail.data) setDraft(detail.data);
  }, [detail.data]);

  async function handleCreate() {
    if (!predmet.trim()) return;
    await create.mutateAsync({
      predmet: predmet.trim(),
      popis: popis.trim(),
      kategorie,
      priorita,
      adresat: adresat.trim(),
      termin_odpovedi: termin.trim(),
      odkaz_vykres: odkazVykres.trim(),
    });
    setOpenNew(false);
    setPredmet('');
    setPopis('');
    setKategorie('projekt');
    setPriorita('stredni');
    setAdresat('');
    setTermin('');
    setOdkazVykres('');
  }

  async function handleStavChange(newStav: RfiStav) {
    if (!draft) return;
    await update.mutateAsync({ rfiId: draft.id, payload: { stav: newStav } });
  }

  async function handleSaveEdit() {
    if (!draft) return;
    await update.mutateAsync({
      rfiId: draft.id,
      payload: {
        predmet: draft.predmet,
        popis: draft.popis ?? '',
        kategorie: draft.kategorie,
        priorita: draft.priorita,
        adresat: draft.adresat,
        odkaz_vykres: draft.odkaz_vykres ?? '',
        termin_odpovedi: draft.termin_odpovedi,
        dopad_harmonogram: draft.dopad_harmonogram ?? '',
        dopad_naklady: draft.dopad_naklady ?? '',
        odpoved: draft.odpoved ?? '',
        zodpovedel: draft.zodpovedel ?? '',
        poznamka: draft.poznamka ?? '',
      },
    });
  }

  async function handleDelete() {
    if (!draft) return;
    if (!window.confirm(`Opravdu smazat dotaz č. ${String(draft.cislo).padStart(3, '0')}?`)) return;
    await del.mutateAsync(draft.id);
    setEditId(null);
  }

  const columns: ListColumn<Rfi>[] = [
    { key: 'cislo', label: 'Č.', width: '100px', mono: true, dim: true, render: (r) => `Dotaz ${String(r.cislo).padStart(3, '0')}` },
    { key: 'predmet', label: 'Dotaz', primary: true },
    { key: 'kategorie', label: 'Kategorie', width: '120px', dim: true, render: (r) => KATEGORIE_LABEL[r.kategorie] ?? r.kategorie },
    { key: 'adresat', label: 'Adresát', width: '180px', dim: true, render: (r) => r.adresat || '—' },
    { key: 'termin_odpovedi', label: 'Termín', width: '120px', mono: true, render: (r) => r.termin_odpovedi || '—' },
    { key: 'stav', label: 'Stav', width: '180px', render: (r) => <Badge tone={stavMap[r.stav]?.tone ?? 'neutral'} pill dot>{stavMap[r.stav]?.label ?? r.stav}</Badge> },
  ];

  if (!activeStavba) {
    return (
      <ListPage breadcrumb={['Realizace', 'Dotazy k projektu']} title="Dotazy k projektu (na projektanta, TDS, investora)">
        <StavbaGate message="Dotazy k projektu se evidují k jedné stavbě." />
      </ListPage>
    );
  }

  return (
    <>
      <ListPage
        breadcrumb={['Realizace', activeStavba.nazev, 'Dotazy k projektu']}
        title="Dotazy k projektu (na projektanta, TDS, investora)"
        subtitle={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} dotazů`}
        status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
        statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} dotazů`}
        actions={<Button variant="primary" onClick={() => setOpenNew(true)}>+ Nový dotaz</Button>}
        searchPlaceholder="Hledat dotaz…"
      >
        {error ? (
          <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
            Chyba: {error instanceof Error ? error.message : 'neznámá'}
          </div>
        ) : (
          <ListTable
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            onRowClick={(r) => setEditId(r.id)}
            emptyTitle="Žádné dotazy"
            emptyText="Dotazy k projektu se budou zobrazovat zde."
          />
        )}
      </ListPage>

      {/* Vytvořit */}
      <Modal
        open={openNew}
        onClose={() => setOpenNew(false)}
        title="Nový dotaz k projektu"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenNew(false)}>Zrušit</Button>
            <Button variant="primary" onClick={handleCreate} disabled={create.isPending || !predmet.trim()}>
              {create.isPending ? 'Ukládám…' : 'Odeslat dotaz'}
            </Button>
          </>
        }
      >
        <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <DocBlock idx="01" title="Dotaz">
            <DocRow k="Předmět *" value={predmet} onChange={setPredmet} placeholder="Jaká má být skladba podlahy v 1.NP?" />
            <DocRow
              k="Kategorie"
              v={
                <select
                  value={kategorie}
                  onChange={(e) => setKategorie(e.target.value as RfiKategorie)}
                  style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  <option value="projekt">Projekt</option>
                  <option value="material">Materiál</option>
                  <option value="termin">Termín</option>
                  <option value="technologie">Technologie</option>
                  <option value="jine">Jiné</option>
                </select>
              }
            />
            <DocRow
              k="Priorita"
              v={
                <select
                  value={priorita}
                  onChange={(e) => setPriorita(e.target.value as Priorita)}
                  style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  <option value="nizka">Nízká</option>
                  <option value="stredni">Střední</option>
                  <option value="vysoka">Vysoká</option>
                </select>
              }
            />
            <DocRow k="Adresát" value={adresat} onChange={setAdresat} placeholder="Projektant / TDS / investor…" />
            <DocRow k="Odkaz na výkres" value={odkazVykres} onChange={setOdkazVykres} placeholder="č. výkresu / strana PD" />
            <DocRow k="Termín odpovědi" mono value={termin} onChange={setTermin} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={termin} onChange={setTermin} />} />
            <DocRow k="Podrobnosti" multiline value={popis} onChange={setPopis} placeholder="Kontext dotazu, odkaz na výkres, apod." />
          </DocBlock>
        </div>
        {create.isError && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 13 }}>
            Uložení selhalo: {create.error instanceof Error ? create.error.message : 'neznámá chyba'}
          </div>
        )}
      </Modal>

      {/* Detail/Edit */}
      <Modal
        open={!!editId}
        onClose={() => setEditId(null)}
        title={draft ? `Dotaz ${String(draft.cislo).padStart(3, '0')}: ${draft.predmet}` : 'Dotaz k projektu'}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditId(null)}>Zavřít</Button>
            <Button variant="danger" onClick={handleDelete} disabled={del.isPending || !draft}>
              {del.isPending ? 'Mažu…' : '🗑️ Smazat'}
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={update.isPending || !draft}>
              {update.isPending ? 'Ukládám…' : 'Uložit změny'}
            </Button>
          </>
        }
      >
        {detail.isLoading || !draft ? (
          <div style={{ padding: 20, color: 'var(--sd-doc-dim)' }}>Načítám…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Status switcher */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--sd-text-dim)', textTransform: 'uppercase' }}>Stav:</span>
              {(['otevreno', 'zodpovezeno', 'uzavreno'] as RfiStav[]).map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={draft.stav === st ? 'primary' : 'secondary'}
                  onClick={() => handleStavChange(st)}
                  disabled={update.isPending || draft.stav === st}
                >
                  {stavMap[st].label}
                </Button>
              ))}
              {draft.datum_odpovedi && (
                <span style={{ fontSize: 11.5, color: 'var(--sd-text-dimmer)' }}>
                  · odpověděno {new Date(draft.datum_odpovedi).toLocaleDateString('cs-CZ')}
                </span>
              )}
            </div>

            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
              <DocBlock idx="01" title="Dotaz">
                <DocRow k="Předmět" value={draft.predmet} onChange={(v) => setDraft({ ...draft, predmet: v })} />
                <DocRow
                  k="Kategorie"
                  v={
                    <select
                      value={draft.kategorie}
                      onChange={(e) => setDraft({ ...draft, kategorie: e.target.value as RfiKategorie })}
                      style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      <option value="projekt">Projekt</option>
                      <option value="material">Materiál</option>
                      <option value="termin">Termín</option>
                      <option value="technologie">Technologie</option>
                      <option value="jine">Jiné</option>
                    </select>
                  }
                />
                <DocRow
                  k="Priorita"
                  v={
                    <select
                      value={draft.priorita}
                      onChange={(e) => setDraft({ ...draft, priorita: e.target.value as Priorita })}
                      style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      <option value="nizka">Nízká</option>
                      <option value="stredni">Střední</option>
                      <option value="vysoka">Vysoká</option>
                    </select>
                  }
                />
                <DocRow k="Adresát" value={draft.adresat} onChange={(v) => setDraft({ ...draft, adresat: v })} />
                <DocRow k="Odkaz na výkres" value={draft.odkaz_vykres ?? ''} onChange={(v) => setDraft({ ...draft, odkaz_vykres: v })} />
                <DocRow k="Termín odpovědi" mono value={draft.termin_odpovedi} onChange={(v) => setDraft({ ...draft, termin_odpovedi: v })} actions={<DocInlineDate value={draft.termin_odpovedi} onChange={(v) => setDraft({ ...draft, termin_odpovedi: v })} />} />
                <DocRow k="Podrobnosti" multiline rows={4} value={draft.popis ?? ''} onChange={(v) => setDraft({ ...draft, popis: v })} />
              </DocBlock>
            </div>

            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
              <DocBlock idx="02" title="Odpověď">
                <DocRow k="Odpověď" multiline rows={4} value={draft.odpoved ?? ''} onChange={(v) => setDraft({ ...draft, odpoved: v })} placeholder="Text odpovědi / vyjádření…" />
                <DocRow k="Zodpověděl" value={draft.zodpovedel ?? ''} onChange={(v) => setDraft({ ...draft, zodpovedel: v })} placeholder="Jméno, role" />
              </DocBlock>
            </div>

            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
              <DocBlock idx="03" title="Dopady & poznámky">
                <DocRow k="Dopad — harmonogram" multiline rows={2} value={draft.dopad_harmonogram ?? ''} onChange={(v) => setDraft({ ...draft, dopad_harmonogram: v })} placeholder="např. posun o 5 dní…" />
                <DocRow k="Dopad — náklady" multiline rows={2} value={draft.dopad_naklady ?? ''} onChange={(v) => setDraft({ ...draft, dopad_naklady: v })} placeholder="např. +25 000 Kč…" />
                <DocRow k="Poznámka" multiline rows={2} value={draft.poznamka ?? ''} onChange={(v) => setDraft({ ...draft, poznamka: v })} />
              </DocBlock>
            </div>

            {update.isError && (
              <div style={{ padding: 10, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 12.5 }}>
                Chyba: {update.error instanceof Error ? update.error.message : 'neznámá'}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
